import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaSearch, FaExternalLinkAlt, FaExclamationTriangle, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa'
import { getStatusPhaseColor, resolveWfLabelKey } from '../../lib/workflowMapping'

const ESTIMATES_BOARD_ID = '18396590426'
const CLIENT_NAME_COL = 'text_mkt9adtr'
const OFFICE_COL      = 'color_mkt9jxhh'
const WORKFLOW_COL    = 'dropdown_mm5kc3kb'

function getCol(item, id) {
    return item.column_values?.find(c => c.id === id)?.text || ''
}

function resolveOffice(item) {
    const office = getCol(item, OFFICE_COL).toLowerCase()
    if (office.includes('miami')) return 'miami'
    if (office.includes('dallas')) return 'dallas'
    return null
}

const OFFICE_FILTERS = [
    { id: 'miami',  key: 'office_miami',  active: 'bg-blue-600 text-white border-blue-600 shadow-sm',  inactive: 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100' },
    { id: 'dallas', key: 'office_dallas', active: 'bg-amber-500 text-white border-amber-500 shadow-sm', inactive: 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100' },
]

// ── Date formatter ───────────────────────────────────────────────────────────
function formatDate(iso) {
    if (!iso) return ''
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    } catch { return '' }
}

// ── Skeleton ────────────────────────────────────────────────────────────────
function SkeletonRow() {
    return (
        <div className="flex gap-3 items-center px-4 py-3 border-b border-gray-100 animate-pulse">
            <div className="w-8 h-8 bg-gray-200 rounded-full shrink-0" />
            <div className="flex-1 space-y-1.5">
                <div className="h-3.5 bg-gray-200 rounded w-3/4" />
                <div className="w-1/2 h-3 bg-gray-100 rounded" />
            </div>
            <div className="w-20 h-5 bg-gray-200 rounded-full shrink-0" />
        </div>
    )
}

// ── Estimate row ─────────────────────────────────────────────────────────────
function parseWfId(item) {
    const col = item.column_values?.find(c => c.id === WORKFLOW_COL)
    if (!col?.value) return null
    try {
        const parsed = JSON.parse(col.value)
        return parsed?.ids?.[0] ?? null
    } catch { return null }
}

function EstimateRow({ item, groupTitle, onSelect }) {
    const { t } = useTranslation()
    const clientName = getCol(item, CLIENT_NAME_COL)
    const office     = getCol(item, OFFICE_COL)
    const date       = formatDate(item.created_at)
    const initial    = (clientName || item.name || '?')[0].toUpperCase()
    const mondayUrl  = `https://jdrivero.monday.com/boards/${ESTIMATES_BOARD_ID}/pulses/${item.id}`
    const wfId       = parseWfId(item)
    const wfLabelKey = resolveWfLabelKey(ESTIMATES_BOARD_ID, wfId)
    const wfText     = getCol(item, WORKFLOW_COL) || (wfLabelKey ? t(wfLabelKey) : '')
    const badgeColor = getStatusPhaseColor({ statusWfId: wfId, boardId: ESTIMATES_BOARD_ID })

    const handleClick = (e) => {
        if (onSelect) {
            e.preventDefault()
            onSelect(item)
        }
    }

    return (
        <a
            href={mondayUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            className="flex gap-3 items-center px-4 py-3 border-b border-gray-100 transition-colors last:border-b-0 hover:bg-gray-50 group cursor-pointer"
        >
            {/* Avatar */}
            <div className="flex justify-center items-center w-8 h-8 text-sm font-bold text-violet-600 bg-violet-100 rounded-full shrink-0">
                {initial}
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                    {clientName || <span className="italic text-gray-400">{t('admin.workflow.no_client')}</span>}
                </p>
                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
                    <FaMapMarkerAlt size={10} className="shrink-0" />
                    <span className="truncate">{item.name || '—'}</span>
                    {office && (
                        <>
                            <span className="shrink-0">·</span>
                            <span className="shrink-0">{office}</span>
                        </>
                    )}
                </div>
            </div>

            {/* Right side */}
            <div className="flex flex-col gap-1 items-end shrink-0">
                {wfText && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badgeColor}`}>
                        {wfText}
                    </span>
                )}
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 text-violet-600 border border-violet-200">
                    {groupTitle}
                </span>
                {date && (
                    <span className="flex items-center gap-1 text-[10px] text-gray-400">
                        <FaCalendarAlt size={8} />
                        {date}
                    </span>
                )}
            </div>

            {/* External link icon on hover */}
            <FaExternalLinkAlt size={11} className="ml-1 text-gray-300 transition-colors group-hover:text-violet-400 shrink-0" />
        </a>
    )
}

// ── Main component (pure display — data fetched/cached by WorkflowMap) ──────
export default function EstimatesPicker({ data = { groups: [], items: [] }, loading = false, error = null, onSelect }) {
    const { t } = useTranslation()
    const [officeFilter, setOfficeFilter] = useState(null)
    const [groupFilter, setGroupFilter] = useState(null)
    const [search, setSearch]     = useState('')

    // Reset group filter when office changes to avoid stale 0-result state
    const handleOfficeFilter = (id) => {
        setOfficeFilter(id)
        setGroupFilter(null)
    }

    // Items scoped to active office filter — used for group chip counts
    const officeFiltered = useMemo(() =>
        officeFilter ? data.items.filter(i => resolveOffice(i) === officeFilter) : data.items
    , [data.items, officeFilter])

  const filteredItems = useMemo(() => {
    let result = officeFiltered
    if (groupFilter) result = result.filter(i => i.group?.id === groupFilter)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      let regex;
      try { regex = new RegExp(q, 'i') } catch { regex = null }
      result = result.filter(i => {
        const client = getCol(i, CLIENT_NAME_COL)
        const name = (i.name || '')
        return regex
          ? regex.test(client) || regex.test(name)
          : client.toLowerCase().includes(q) || name.toLowerCase().includes(q)
      })
    }
    return result
  }, [officeFiltered, groupFilter, search])

    return (
        <div className="mx-auto max-w-2xl lg:max-w-5xl">
            {/* Search */}
            <div className="relative mb-3">
                <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('admin.workflow.search_est')}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-300/60 transition shadow-sm"
                />
                {search && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">
                        {filteredItems.length} de {officeFiltered.length}
                    </span>
                )}
            </div>

            {/* Office filter chips */}
            {data.items.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1 mb-2 scrollbar-none">
                    <button
                        onClick={() => handleOfficeFilter(null)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                            officeFilter === null
                                ? 'bg-gray-700 text-white border-gray-700 shadow-md'
                                : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                        }`}
                    >
                        {t('admin.workflow.filter_all')}
                        <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{data.items.length}</span>
                    </button>
                    {OFFICE_FILTERS.map(of => {
                        const count = data.items.filter(i => resolveOffice(i) === of.id).length
                        const isActive = officeFilter === of.id
                        return (
                            <button
                                key={of.id}
                                onClick={() => handleOfficeFilter(isActive ? null : of.id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                                    isActive ? of.active : of.inactive
                                }`}
                            >
                                {t(`admin.workflow.${of.key}`)}
                                <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{count}</span>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Group filter chips */}
            {data.groups.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3 scrollbar-none">
                    <button
                        onClick={() => setGroupFilter(null)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                            groupFilter === null
                                ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                                : 'bg-violet-50 text-violet-600 border-violet-200 hover:bg-violet-100'
                        }`}
                    >
                        {t('admin.workflow.filter_all')}
                        <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{officeFiltered.length}</span>
                    </button>
                    {data.groups.map(g => {
                        const count = officeFiltered.filter(i => i.group?.id === g.id).length
                        const isActive = groupFilter === g.id
                        return (
                            <button
                                key={g.id}
                                onClick={() => setGroupFilter(isActive ? null : g.id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border shrink-0 transition-all ${
                                    isActive
                                        ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                                        : 'bg-violet-50 text-violet-600 border-violet-200 hover:bg-violet-100'
                                }`}
                            >
                                {g.title}
                                <span className="px-1 rounded-full bg-black/10 text-[10px] leading-4">{count}</span>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="flex gap-2 items-center p-4 text-sm text-red-600 bg-red-50 rounded-xl border border-red-200">
                    <FaExclamationTriangle size={14} />
                    {error}
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                    {[...Array(8)].map((_, i) => <SkeletonRow key={i} />)}
                </div>
            )}

            {/* Results */}
            {!loading && !error && (
                filteredItems.length === 0
                    ? (
                        <div className="py-12 text-sm text-center text-gray-400">
                            {t('admin.workflow.est_empty')}
                        </div>
                    )
                    : (
                        <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                            {filteredItems.map(item => (
                                <EstimateRow
                                    key={item.id}
                                    item={item}
                                    groupTitle={item.group?.title || '—'}
                                    onSelect={onSelect}
                                />
                            ))}
                        </div>
                    )
            )}
        </div>
    )
}
